// src/components/pages/Verification.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

import AppShell from "../layout/AppShell";
import Logo from "../layout/Logo";
import Button from "../ui/Button";
import Card from "../ui/Card";
import Icon from "../ui/Icon";
import { Toast } from "../ui/Spinner";

import axiosInstance from "../../utils/AxiosInstance";
import {
  clearPendingVerificationEmail,
  getPendingVerificationEmail,
  setPendingVerificationEmail,
} from "../../utils/authStorage";

/**
 * Email-code verification. Handles two operations:
 *   - "verification":   PUT /site/user/confirm-create
 *   - "password-reset": POST /site/user/confirm-code-change-password/:email
 *
 * Email source priority: router state -> ?email query -> localStorage.
 */
export default function Verification() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const emailFromState = String(location.state?.email || "").trim().toLowerCase();
  const emailFromQuery = String(searchParams.get("email") || "").trim().toLowerCase();
  const operation =
    location.state?.operation === "password-reset" ? "password-reset" : "verification";
  const persisted = operation === "verification" ? getPendingVerificationEmail() : "";
  const email = emailFromState || emailFromQuery || persisted || "";

  const [digits, setDigits] = useState(Array(6).fill(""));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, tone: "success", message: "" });
  const [successCountdown, setSuccessCountdown] = useState(0);

  const inputsRef = useRef([]);

  // Persist email for return visits in the "verification" flow
  useEffect(() => {
    if (operation === "verification" && email) setPendingVerificationEmail(email);
  }, [operation, email]);

  // For password-reset, auto-send the code when this screen first opens
  useEffect(() => {
    if (operation !== "password-reset" || !email) return;
    (async () => {
      try {
        await axiosInstance.post(
          "/site/user/forgot-password",
          { email },
          { headers: { "Content-Type": "application/json" } }
        );
        showToast(`We sent a verification code to ${email}.`, "success");
      } catch {
        showToast("Failed to send verification code. You can resend below.", "error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation, email]);

  // Post-success countdown -> /login
  useEffect(() => {
    if (successCountdown <= 0) return;
    if (successCountdown === 1) {
      const t = setTimeout(() => navigate("/login"), 1000);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setSuccessCountdown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [successCountdown, navigate]);

  const showToast = (message, tone = "success") => {
    setToast({ open: true, tone, message });
    setTimeout(() => setToast((t) => ({ ...t, open: false })), 3500);
  };

  const onDigitChange = (idx, value) => {
    const v = value.replace(/\D/g, "").slice(-1);
    setDigits((d) => {
      const next = [...d];
      next[idx] = v;
      return next;
    });
    if (v && idx < 5) inputsRef.current[idx + 1]?.focus();
    setError("");
  };

  const onDigitKey = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  const onPaste = (e) => {
    const pasted = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    const next = pasted.split("").concat(Array(6).fill("")).slice(0, 6);
    setDigits(next);
    inputsRef.current[Math.min(pasted.length, 5)]?.focus();
  };

  const code = digits.join("");

  const handleVerify = async () => {
    if (code.length !== 6) {
      setError("Enter the full 6-digit code.");
      return;
    }
    if (!email) {
      setError("We could not find the email for this verification.");
      return;
    }
    setLoading(true);
    setError("");

    try {
      if (operation === "verification") {
        const { data } = await axiosInstance.put(
          "/site/user/confirm-create",
          { email, code },
          { headers: { "Content-Type": "application/json" } }
        );
        if (data?.active === "TRUE" || data?.active === "true" || data?.active === true) {
          clearPendingVerificationEmail();
          setSuccessCountdown(5);
        } else {
          setError("Invalid verification code.");
        }
      } else {
        const res = await axiosInstance.post(
          `/site/user/confirm-code-change-password/${encodeURIComponent(email)}`,
          { code },
          { headers: { "Content-Type": "application/json" } }
        );
        if (res.status === 200) {
          navigate("/reset-password", { state: { email, code } });
        }
      }
    } catch (err) {
      const data = err?.response?.data;
      if (typeof data === "string" && data.includes("code not found")) {
        setError("Invalid verification code.");
      } else if (Array.isArray(data?.parameterViolations)) {
        setError(data.parameterViolations.map((v) => v.message).join("; "));
      } else {
        setError("Verification failed. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) return;
    setResendLoading(true);
    try {
      if (operation === "password-reset") {
        await axiosInstance.post(
          "/site/user/forgot-password",
          { email },
          { headers: { "Content-Type": "application/json" } }
        );
      } else {
        await axiosInstance.post("/site/user/resend-email-code", { email });
      }
      showToast("Verification email resent. Check your inbox and spam or junk folder.", "success");
    } catch {
      showToast("Failed to resend. Try again in a moment.", "error");
    } finally {
      setResendLoading(false);
    }
  };

  const heading =
    operation === "password-reset"
      ? "Enter the code to reset your password."
      : "Verify your email.";
  const sub =
    operation === "password-reset"
      ? "We emailed a 6-digit code. It expires in 10 minutes."
      : "We emailed you a 6-digit code. It expires in 10 minutes.";

  return (
    <AppShell bare>
      <Toast open={toast.open} tone={toast.tone} message={toast.message} />

      <div className="min-h-screen flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-[440px]">
          <div className="flex justify-center mb-6">
            <Logo size="lg" onClick={() => navigate("/")} />
          </div>

          <Card>
            <h1
              className="font-display font-semibold mb-1.5"
              style={{ fontSize: 24, color: "var(--txt)" }}
            >
              {heading}
            </h1>
            <p className="text-[13px] mb-1" style={{ color: "var(--txt-2)" }}>
              {sub}
            </p>
            {email && (
              <p className="text-[12px] mb-2 break-all" style={{ color: "var(--txt-3)" }}>
                Sent to <span style={{ color: "var(--txt-2)" }}>{email}</span>
              </p>
            )}

            {/* Spam folder notice */}
            <div
              className="mb-5 px-3 py-2 rounded-[8px] text-[12px] flex items-start gap-2"
              style={{
                background: "var(--surf-2)",
                border: "1px solid var(--border-2)",
                color: "var(--txt-2)",
              }}
            >
              <Icon name="info" size={13} style={{ marginTop: 2, flexShrink: 0 }} />
              <span>
                If you don't see the email in a minute or two, please check your
                <strong style={{ color: "var(--txt)" }}> Spam </strong>
                or
                <strong style={{ color: "var(--txt)" }}> Junk </strong>
                folder.
              </span>
            </div>

            <div className="flex justify-between gap-1.5 sm:gap-2 mb-1.5" onPaste={onPaste}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (inputsRef.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={d}
                  onChange={(e) => onDigitChange(i, e.target.value)}
                  onKeyDown={(e) => onDigitKey(i, e)}
                  className="w-full aspect-square text-center font-display font-semibold text-[22px] rounded-[10px] outline-none transition-colors"
                  style={{
                    background: "var(--surf-2)",
                    color: "var(--txt)",
                    border: `1px solid ${
                      error ? "var(--red-border)" : "var(--border-2)"
                    }`,
                    maxWidth: 56,
                  }}
                />
              ))}
            </div>

            {error && (
              <p
                className="mb-3 text-[12px] flex items-center gap-1.5"
                style={{ color: "var(--red)" }}
              >
                <Icon name="alert-circle" size={13} />
                {error}
              </p>
            )}

            <Button
              full
              loading={loading}
              onClick={handleVerify}
              icon="check"
              className="mt-3"
            >
              {loading ? "Verifying..." : "Verify"}
            </Button>

            <div className="mt-4 flex items-center justify-between text-[12px]">
              <span style={{ color: "var(--txt-2)" }}>Didn't get it?</span>
              <button
                type="button"
                onClick={handleResend}
                disabled={resendLoading}
                className="font-medium hover:underline disabled:opacity-50"
                style={{ color: "var(--gold-lt)" }}
              >
                {resendLoading ? "Resending..." : "Resend code"}
              </button>
            </div>
          </Card>
        </div>
      </div>

      {/* Success modal */}
      {successCountdown > 0 && (
        <div
          className="fixed inset-0 z-[150] flex items-center justify-center px-4"
          style={{ background: "rgba(7,9,15,0.85)", backdropFilter: "blur(8px)" }}
        >
          <Card className="max-w-[380px] text-center">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{
                background: "var(--teal-dim)",
                border: "1px solid var(--teal-border)",
                color: "var(--teal)",
                animation: "checkPop 0.5s cubic-bezier(.175,.885,.32,1.275) forwards",
              }}
            >
              <Icon name="check" size={28} />
            </div>
            <h2
              className="font-display font-semibold mb-2"
              style={{ fontSize: 22, color: "var(--txt)" }}
            >
              Email verified!
            </h2>
            <p className="text-[13px] mb-5" style={{ color: "var(--txt-2)" }}>
              Redirecting you to log in in {successCountdown}s...
            </p>
            <Button full onClick={() => navigate("/login")}>
              Go to login now
            </Button>
          </Card>
        </div>
      )}
    </AppShell>
  );
}
